import { notify } from '@/plugins/notify'

// Server-paginated, server-sorted list pages. The component implements
// fetchPage({ page, perPage, filters, sortOption, order }) and binds its table with
// v-bind="tableBindings" @update:options="onTableOptions". Header values are the backend sort keys.
// scopeToDealer makes dealers only ever request their own records.
export default {
    data() {
        return {
            items: [],
            page: 1,
            pageCount: 0,
            total: 0,
            itemsPerPage: 10,
            loading: false,
            filters: {},
            sortOption: null,
            order: 'ASC',
            scopeToDealer: false,
            // the list's own path, fixed at creation so route changes away from the list are ignored
            listKey: null,
            requestId: 0
        }
    },
    computed: {
        listState() {
            return { filters: this.filters, sortOption: this.sortOption, order: this.order }
        },
        isAdmin() {
            return this.$store.getters.isAdmin
        },
        isDealer() {
            return this.$store.getters.isDealer
        },
        // dealers can only look, so they never get the edit/delete column
        visibleHeaders() {
            return this.isAdmin ? this.headers : this.headers.filter(header => header.value !== 'actions')
        },
        // props for v-data-table: the server already paginated and sorted the rows
        tableBindings() {
            return {
                items: this.items,
                loading: this.loading,
                itemsPerPage: this.itemsPerPage,
                serverItemsLength: this.total,
                sortBy: this.sortOption ? [this.sortOption] : [],
                sortDesc: this.sortOption ? [this.order === 'DESC'] : [],
                hideDefaultFooter: true,
                itemKey: 'id'
            }
        },
        totalLabel() {
            if (this.loading && !this.total) {
                return 'Loading…'
            }
            return `${this.total} ${this.total === 1 ? 'result' : 'results'}`
        }
    },
    watch: {
        page(value) {
            if (this.routePage() !== value) {
                this.$router.push({ query: { ...this.$route.query, page: String(value) } }).catch(() => {})
            }
        },
        '$route.query.page'() {
            if (this.$route.path !== this.listKey) {
                return
            }
            const page = this.routePage()
            if (page !== this.page) {
                this.page = page
            }
            this.fetchItems()
        }
    },
    created() {
        this.listKey = this.$route.path
        const state = this.$store.getters.getListState(this.listKey)
        this.filters = { ...state.filters }
        this.sortOption = state.sortOption || null
        this.order = state.order || 'ASC'
        this.page = this.routePage()
        this.fetchItems()
    },
    methods: {
        routePage() {
            return parseInt(this.$route.query.page) || 1
        },
        requestFilters() {
            const filters = { ...this.filters }
            if (this.scopeToDealer && this.isDealer) {
                filters.dealer = this.$store.getters.dealerId
            }
            return filters
        },
        fetchItems() {
            const requestId = ++this.requestId
            this.loading = true
            return this.fetchPage({
                page: this.page,
                perPage: this.itemsPerPage,
                filters: this.requestFilters(),
                sortOption: this.sortOption,
                order: this.order
            }).then(resp => {
                if (requestId !== this.requestId) {
                    return
                }
                const data = resp.data || {}
                this.items = data.data || []
                this.total = data.total || 0
                this.pageCount = data.last_page || 0
                if (this.onPageLoaded) {
                    this.onPageLoaded(data)
                }
                if (this.pageCount && this.page > this.pageCount) {
                    this.page = 1
                }
            }).catch(error => {
                if (requestId !== this.requestId) {
                    return
                }
                this.items = []
                this.total = 0
                this.pageCount = 0
                if (this.onPageLoaded) {
                    this.onPageLoaded({})
                }
                notify.error(error, 'Could not load the list')
            }).finally(() => {
                if (requestId === this.requestId) {
                    this.loading = false
                }
            })
        },
        // clicking a column header: ascending, descending, then back to the default order
        onTableOptions({ sortBy, sortDesc }) {
            const sortOption = sortBy[0] || null
            const order = sortOption && sortDesc[0] ? 'DESC' : 'ASC'
            if (sortOption === this.sortOption && order === this.order) {
                return
            }
            this.applyFilters({ filters: this.filters, sortOption, order })
        },
        applyFilters({ filters, sortOption = this.sortOption, order = this.order }) {
            this.filters = { ...filters }
            this.sortOption = sortOption || null
            this.order = order || 'ASC'
            this.$store.dispatch('setListState', { key: this.listKey, value: this.listState })
            if (this.page !== 1) {
                this.page = 1
            } else {
                this.fetchItems()
            }
        },
        openItem(item, mode = 'show') {
            this.$router.push({ path: `${this.listKey}/${item.id}/${mode}`, query: { page: String(this.page) } })
        },
        createItem() {
            this.$router.push({ path: `${this.listKey}/create`, query: { page: String(this.page) } })
        },
        deleteItem(item, deleteFn, label = 'Item') {
            return deleteFn(item.id).then(() => {
                notify.success(`${label} deleted`)
                if (this.items.length === 1 && this.page > 1) {
                    this.page -= 1
                } else {
                    this.fetchItems()
                }
            }).catch(error => notify.error(error, `Could not delete the ${label.toLowerCase()}`))
        }
    }
}
